import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Button, message, Card } from "antd";
import btnStyles from "../../../styles/Custom/Button.module.css";
import "./RecordModal.css"; // 🔥 Import custom CSS
import { doctorprocessOrderAPI } from "../../../services/doctor.services";
import AiMedForm from "./AiMedForm";
import dayjs from "dayjs";
import AiVitalForm from "./AiVitals/AiVitalForm";
import AiGrbsForm from "./AiVitals/AiGRBS";
import AiInvestigationForm from "./AiInvestigations/AiInvestigationForm";
import useQuery from "../../../hooks/useQuery";
import AiCareItems from "./AiCareItems/AiCareItems";
import useIsMobile from "../../../hooks/useIsMobile";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../IpEMR/Doctor/InitialInspection/ClosePopup";
import "../../IpEMR/Doctor/InitialInspection/compactModal.css";

export default function RecordModal({
  open,
  setOpen,
  aiResponse: initialAiResponse,
  startInFormStep = false,
  refreshPage,
}) {
  const params = useQuery();
  const isMobile = useIsMobile();
  const [aiResponse, setAiResponse] = useState(initialAiResponse);
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const [showGrbs, setShowGrbs] = useState(true);
  const [showVitals, setShowVitals] = useState(true);
  const [showMedicines, setShowMedicines] = useState(true);
  const [showInvestigations, setShowInvestigations] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    if (aiResponse) {
      setShowGrbs(aiResponse?.monitoring?.grbs?.frequency !== null);
      setShowVitals(aiResponse?.monitoring?.vitals?.frequency !== null);
      setShowMedicines(aiResponse?.medications?.length > 0);
      setShowInvestigations(aiResponse?.investigations?.length > 0);
    }
  }, [aiResponse]);

  const onSubmit = (values) => {
    setBtnLoading(true);

    const cleanedValues = Object.fromEntries(
      Object.entries(values).filter(
        ([key]) => !key.startsWith("schedule.times.")
      )
    );

    const formatTimes = (item) => ({
      ...item,
      schedule: {
        ...item.schedule,
        times: item?.schedule?.times?.map((t) => dayjs(t).format("HH")),
      },
    });

    let payload = { ...cleanedValues };

    // MEDICATIONS
    if (Array.isArray(cleanedValues.medications)) {
      if (cleanedValues.medications.length > 0) {
        payload.medications = cleanedValues.medications.map((m) =>
          formatTimes(m)
        );
      } else {
        delete payload.medications;
      }
    }

    // INVESTIGATIONS
    if (Array.isArray(cleanedValues.investigations)) {
      if (cleanedValues.investigations.length > 0) {
        payload.investigations = cleanedValues.investigations.map((inv) => ({
          id: inv.investigation_id || inv.id,
          name: inv.name,
          comments: inv.comments || "",
        }));
      } else {
        delete payload.investigations;
      }
    }

    // CARE ITEMS
    if (cleanedValues.care_items) {
      const careItemPayload = {};

      Object.entries(cleanedValues.care_items).forEach(([key, value]) => {
        const simpleYesItems = [
          "ostomy_care",
          "eye_care_trasorrhaphy",
          "eye_care_eye_patching_guard",
        ];

        const finalData = simpleYesItems.includes(key)
          ? { [key]: "yes" }
          : value?.data || {};

        careItemPayload[key] = {
          action: "start",
          care_type: key,
          admission_id: params?.patient,
          data: finalData,
        };
      });

      payload.care_items = careItemPayload;
    }

    doctorprocessOrderAPI(params?.patient, payload)
      .then((res) => {
        message.success(res?.data?.message);
        setOpen(false);
        refreshPage();
      })
      .catch((err) => {
        console.log(err);
        const issues = err?.response?.data?.issues;

        if (issues) {
          const errorMsg = Object.values(issues).join(", ");
          message.error(
            errorMsg || err?.response?.data?.message || "Something went wrong"
          );
        } else {
          message.error(err?.response?.data?.message || "Something went wrong");
        }
      })
      .finally(() => setBtnLoading(false));
  };

  return (
    <>
      {/* Custom Semi-transparent Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.25)",
          zIndex: 999,
          pointerEvents: "none", // 🔥 Allow scrolling behind
        }}
      />

      <Modal
        title="Updated Prescription"
        width={isMobile ? "calc(100vw - 32px)" : "85%"}
        open={open}
        onCancel={() => setOpen(false)}
        mask={false} // 🔥 Disable default mask
        centered={isMobile}
        style={{ pointerEvents: "auto" }} // 🔥 But modal itself is clickable
        wrapClassName={
          isMobile
            ? `${NURSE_IP_COMPACT_MODAL_WRAP} scrollable-modal`
            : "scrollable-modal"
        }
        getContainer={() => document.body}
        footer={
          <div className="flex-end" style={{ gap: "24px" }}>
            <Form.Item>
              <Button
                type="primary"
                ghost
                className={btnStyles.mdBtn}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                loading={btnLoading}
                htmlType="submit"
                type="primary"
                className={btnStyles.mdBtn}
                onClick={() => {
                  if (formRef.current) {
                    formRef.current.submit();
                  }
                }}
              >
                Submit
              </Button>
            </Form.Item>
          </div>
        }
        maskClosable={false}
      >
        <Form layout="vertical" ref={formRef} form={form} onFinish={onSubmit}>
          <div
            style={{
              maxHeight: isMobile ? "min(70dvh, calc(100dvh - 160px))" : "50vh",
              overflowY: "auto",
            }}
          >
            {showMedicines && (
              <Card title="Add Medicine">
                <AiMedForm
                  form={form}
                  editData={aiResponse}
                  onLastDelete={() => setShowMedicines(false)}
                />
              </Card>
            )}

            {Object?.keys(aiResponse?.care_items || {})?.length > 0 && (
              <Card title="Care Items">
                <AiCareItems
                  editData={aiResponse}
                  form={form}
                  setEditData={setAiResponse}
                />
              </Card>
            )}

            {(showGrbs || showVitals) && (
              <Card title="Monitoring">
                {showGrbs && (
                  <AiGrbsForm
                    form={form}
                    onDelete={() => setShowGrbs(false)}
                    editData={aiResponse}
                  />
                )}

                {showVitals && (
                  <AiVitalForm
                    form={form}
                    onDelete={() => setShowVitals(false)}
                    editData={aiResponse}
                  />
                )}
              </Card>
            )}

            {showInvestigations && aiResponse?.investigations?.length > 0 && (
              <Card title="Add Investigations">
                <AiInvestigationForm
                  form={form}
                  editData={aiResponse}
                  onLastDelete={() => setShowInvestigations(false)}
                />
              </Card>
            )}
          </div>
        </Form>
      </Modal>
    </>
  );
}
